import { ArrowLeftOutlined, AudioOutlined, FormOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Space, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { AdminAccessRoleEnum, useGetOneAdminAccessQuery } from 'api/AdminAccessApi';
import { useGetAllMessagesQuery } from 'api/AdminAccessMessagesApi';
import { Contact } from 'api/ContactsApi';
import { Message, MessageStatusEnum, MessageTypeEnum } from 'api/MessagesApi';
import { ResponsiveTable } from 'components/UiKit/ResponsiveTable/ResponsiveTable';
import UserAvatar from 'components/UiKit/UserAvatar/UserAvatar';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { displayDeliveryDate, getAdminAccessDisplayName } from 'utils/utils';
import './BeneficiaryComponent.css';

const PAGE_SIZE = 10;

function BeneficiaryComponent() {
  const navigate = useNavigate();
  const { adminAccessId } = useParams();

  const [currentPage, setCurrentPage] = useState(1);
  const { data: adminAccess } = useGetOneAdminAccessQuery({ id: adminAccessId! }, { skip: !adminAccessId });
  const {
    data: messagesData,
    isFetching,
    isError,
    error
  } = useGetAllMessagesQuery(
    {
      id: adminAccessId!,
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE
    },
    { skip: !adminAccessId }
  );

  const onTableChanged = (pagination) => {
    setCurrentPage(pagination.current);
  };

  const renderTypeIcon = (type: MessageTypeEnum) => {
    if (type === MessageTypeEnum.Video) {
      return <VideoCameraOutlined />;
    } else if (type === MessageTypeEnum.Audio) {
      return <AudioOutlined />;
    } else {
      return <FormOutlined />;
    }
  };

  const renderContacts = (contacts: Contact[]) => {
    return (
      contacts &&
      contacts.map((contact) => (
        <div key={contact.id}>
          <Typography.Text>
            {contact.name} ({contact.email})
          </Typography.Text>
        </div>
      ))
    );
  };

  const goToMessageDetails = (messageId: string) => {
    navigate(`/beneficiaries/${adminAccessId}/message/${messageId}`);
  };

  const goBack = () => {
    navigate(`/beneficiaries`);
  };

  const renderStatus = (status: MessageStatusEnum) => {
    const statuses = {
      [MessageStatusEnum.Published]: <Tag color="warning">Scheduled</Tag>,
      [MessageStatusEnum.Delivering]: <Tag color="geekblue">Delivering</Tag>,
      [MessageStatusEnum.Delivered]: <Tag color="geekblue">Delivered</Tag>
    };

    return statuses[status] ? statuses[status] : <Tag color="default">Draft</Tag>;
  };

  const mobileColumns: ColumnsType<Message> = [
    {
      render: (message: Message) => (
        <Space
          size={4}
          direction="vertical"
          className="responsive-table-card"
          onClick={() => goToMessageDetails(message.id)}
          role="link"
        >
          <Flex justify="space-between">
            <Flex align="center" gap="small">
              {renderTypeIcon(message.type)}
              <span className="responsive-table-title">{message.name}</span>
            </Flex>
            <div>{renderStatus(message.status)}</div>
          </Flex>
          <div>
            <strong>Send To:</strong> {renderContacts(message.contacts)}
          </div>
          <Flex justify="space-between">
            <div>
              <strong>Send On:</strong> {displayDeliveryDate(message)}
            </div>
          </Flex>
        </Space>
      )
    }
  ];

  const columns: ColumnsType<Message> = [
    {
      title: 'Message Name',
      key: 'name',
      render: (_, message) => (
        <div className="message-title">
          <Typography.Link onClick={() => goToMessageDetails(message.id)}>{message.name}</Typography.Link>
        </div>
      )
    },
    {
      title: 'Type',
      key: 'type',
      dataIndex: 'type',
      render: (type) => renderTypeIcon(type)
    },
    {
      title: 'Recipient(s)',
      key: 'recipient',
      dataIndex: 'contacts',
      render: (contacts: Contact[]) => renderContacts(contacts)
    },
    {
      title: 'Delivery Date',
      key: 'delivery',
      render: (_, message) => displayDeliveryDate(message)
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      render: (status: MessageStatusEnum) => renderStatus(status)
    }
  ];

  return (
    <>
      <div className="beneficiary-component">
        <Button className="back-btn" type="text" onClick={() => goBack()}>
          <ArrowLeftOutlined />
          Back to Administration
        </Button>

        <Flex align="center" gap={16} className="beneficiary-header">
          <UserAvatar size={56} user={adminAccess?.user} fallbackType="initials" className="beneficiary-avatar" />
          <div className="beneficiary-info">
            <div className="beneficiary-name">
              {getAdminAccessDisplayName(adminAccess || {}, 'Unknown')}'s Messages
            </div>
            <div className="beneficiary-email">
              {messagesData?.total || 0} messages ·{' '}
              {adminAccess?.role === AdminAccessRoleEnum.Manager ? 'View only + manage delivery' : 'View only'}
            </div>
          </div>
        </Flex>

        {isError && <Alert className="message" message={(error as any)?.data.message} type="error" showIcon />}

        <ResponsiveTable
          mobileUntilBreakpoint="lg"
          mobileColumns={mobileColumns}
          columns={columns}
          dataSource={messagesData?.list || []}
          pagination={{
            defaultPageSize: PAGE_SIZE,
            current: currentPage,
            total: messagesData?.total || 0
          }}
          onChange={onTableChanged}
          rowKey="id"
          bordered
          loading={isFetching}
        />
      </div>
    </>
  );
}

export default BeneficiaryComponent;
